import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from '../api/axiosConfig';
import { AuthContext } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import Pagination from '../components/Pagination';
import PagadosFueraTab from '../components/PagadosFueraTab';
import * as XLSX from 'xlsx';
import './Profiles.css';

// Submódulo "Pagos a Tránsitos": lotes diarios de trámites ejecutados, pago con soporte bancario,
// cuentas (empresa y tránsitos) y tarifas. Módulo aislado: no modifica otros módulos.
const inputStyle = {
  padding: '10px 14px', border: '1px solid var(--input-border)', borderRadius: '10px',
  background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: '13px', fontWeight: '500', outline: 'none', width: '100%'
};
const labelStyle = { fontSize: '11px', fontWeight: '800', color: 'var(--text-secondary)', textTransform: 'uppercase' };
const btn = (bg) => ({ padding: '9px 16px', background: bg, color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 600, cursor: 'pointer', fontSize: '13px' });
const smallBtn = (bg) => ({ ...btn(bg), padding: '6px 12px', fontSize: '12px', borderRadius: '8px' });

const money = (v) => `$ ${Number(v || 0).toLocaleString('es-CO', { maximumFractionDigits: 0 })}`;
const fFecha = (d) => d ? new Date(d).toLocaleDateString('es-CO', { timeZone: 'UTC' }) : '—';
const fFechaHora = (d) => d ? new Date(d).toLocaleString('es-CO', { timeZone: 'America/Bogota', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
const hoy = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
const TIPOS = { moto: 'Moto', carro: 'Carro', moto_carro: 'Moto Carro' };
const ESTADOS = {
  generado: { label: 'Pendiente de pago', color: '#b45309', bg: '#fef3c7' },
  pagado: { label: 'Pagado', color: '#15803d', bg: '#dcfce7' },
  anulado: { label: 'Anulado', color: '#64748b', bg: '#f1f5f9' }
};

const Field = ({ id, label, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
    <label htmlFor={id} style={labelStyle}>{label}</label>
    {children}
  </div>
);

const Modal = ({ title, onClose, children, width = 720 }) => (
  <div className="modal-overlay" role="dialog" aria-modal="true" aria-label={title}>
    <div className="modal-content" style={{ maxWidth: `${width}px`, width: '95%', maxHeight: '88vh', overflowY: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <h3 style={{ margin: 0 }}>{title}</h3>
        <button type="button" onClick={onClose} aria-label="Cerrar" style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>✕</button>
      </div>
      {children}
    </div>
  </div>
);

const PagosTransito = () => {
  const { user } = useContext(AuthContext);
  const isSuperadmin = user?.role === 'superadmin' || user?.profile_name === 'superadmin';
  const isGestor = isSuperadmin || user?.profile_name === 'auxiliar transiapp';

  const [tab, setTab] = useState('lotes');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [transitos, setTransitos] = useState([]);

  // Lotes
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fTransito, setFTransito] = useState('todos');
  const [fEstado, setFEstado] = useState('todos');
  const [fDesde, setFDesde] = useState('');
  const [fHasta, setFHasta] = useState('');
  const [fBuscar, setFBuscar] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  // Cuentas y tarifas
  const [cuentas, setCuentas] = useState({ empresa: [], transito: [] });
  const [tarifas, setTarifas] = useState([]);

  // Modales
  const [liquidar, setLiquidar] = useState(null);   // { transito_id, fecha, preview, loading }
  const [detalle, setDetalle] = useState(null);
  const [pago, setPago] = useState(null);           // { lote, cuenta_origen_id, cuenta_destino_id, fecha_pago, observaciones, file }
  const [anular, setAnular] = useState(null);       // { lote, motivo }
  const [busy, setBusy] = useState(false);
  const [pfRefresh, setPfRefresh] = useState(0);   // fuerza recargar la pestaña "Pagados por fuera"
  const [detFuera, setDetFuera] = useState({ sel: [], motivo: '', ref: '', fecha: '' }); // sacar trámites de un lote aún no pagado
  const [addPl, setAddPl] = useState({ texto: '', motivo: '', manuales: [], validando: false }); // agregar placas a un lote aún no pagado

  const flash = (msg) => { setSuccess(msg); setError(''); setTimeout(() => setSuccess(''), 5000); };
  const fail = (err, def) => { setError(err?.response?.data?.message || def); setSuccess(''); };

  const fetchLotes = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (fTransito !== 'todos') params.append('transito_id', fTransito);
      if (fEstado !== 'todos') params.append('estado', fEstado);
      if (fDesde) params.append('fecha_inicio', fDesde);
      if (fHasta) params.append('fecha_fin', fHasta);
      if (fBuscar.trim()) params.append('search', fBuscar.trim());
      const r = await axios.get(`/api/pagos-transito/lotes?${params.toString()}`);
      setLotes(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      fail(e, 'Error al cargar los lotes');
    } finally {
      setLoading(false);
    }
  }, [fTransito, fEstado, fDesde, fHasta, fBuscar]);

  const fetchCuentas = useCallback(async () => {
    if (!isGestor) return;
    try { setCuentas((await axios.get('/api/pagos-transito/cuentas')).data); } catch (e) { fail(e, 'Error al cargar cuentas'); }
  }, [isGestor]);

  const fetchTarifas = useCallback(async () => {
    if (!isGestor) return;
    try { setTarifas((await axios.get('/api/pagos-transito/tarifas')).data); } catch (e) { fail(e, 'Error al cargar tarifas'); }
  }, [isGestor]);

  useEffect(() => {
    axios.get('/api/pagos-transito/transitos').then(r => setTransitos(r.data)).catch(e => fail(e, 'Error al cargar tránsitos'));
  }, []);
  useEffect(() => { const t = setTimeout(fetchLotes, 300); return () => clearTimeout(t); }, [fetchLotes]);
  useEffect(() => { fetchCuentas(); fetchTarifas(); }, [fetchCuentas, fetchTarifas]);

  const resumen = useMemo(() => {
    const r = { generado: { n: 0, v: 0 }, pagado: { n: 0, v: 0 } };
    lotes.forEach(l => { if (r[l.estado]) { r[l.estado].n++; r[l.estado].v += Number(l.valor_total); } });
    return r;
  }, [lotes]);

  // ---------- Liquidar ----------
  const abrirLiquidar = () => setLiquidar({ transito_id: '', fecha: hoy(), preview: null, loading: false, manualTexto: '', motivoManual: '', manuales: [], validandoManuales: false, sel: [], fueraMotivo: '', fueraRef: '', fueraFecha: '' });
  const calcularPreview = async (transitoId, fecha) => {
    if (!transitoId) { setLiquidar(l => ({ ...l, preview: null })); return; }
    setLiquidar(l => ({ ...l, loading: true }));
    try {
      const r = await axios.get(`/api/pagos-transito/lotes/preview?transito_id=${transitoId}&fecha=${fecha}`);
      setLiquidar(l => ({ ...l, preview: r.data, loading: false, sel: [] }));
    } catch (e) {
      fail(e, 'Error al calcular el lote');
      setLiquidar(l => ({ ...l, loading: false }));
    }
  };
  const confirmarLiquidar = async () => {
    setBusy(true);
    try {
      const placasManuales = (liquidar.manuales || []).filter(m => m.valido).map(m => m.placa);
      const r = await axios.post('/api/pagos-transito/lotes', {
        transito_id: liquidar.transito_id, fecha: liquidar.fecha,
        placas_manuales: placasManuales, motivo_manual: liquidar.motivoManual
      });
      flash(r.data.message);
      setLiquidar(null);
      fetchLotes();
    } catch (e) {
      fail(e, 'Error al generar el lote');
    } finally {
      setBusy(false);
    }
  };

  // ---------- Marcar trámites como pagados por fuera del aplicativo ----------
  const toggleSel = (id) => setLiquidar(l => ({ ...l, sel: l.sel.includes(id) ? l.sel.filter(x => x !== id) : [...l.sel, id] }));
  const toggleSelTodos = () => setLiquidar(l => {
    const ids = (l.preview?.items || []).map(i => i.tramite_id);
    return { ...l, sel: l.sel.length === ids.length ? [] : ids };
  });
  const marcarPagadosFuera = async () => {
    if (!liquidar.sel.length) return;
    if (!String(liquidar.fueraMotivo || '').trim()) { setError('Escriba el motivo (p. ej. "pagado al tránsito antes del módulo")'); return; }
    if (!window.confirm(`¿Marcar ${liquidar.sel.length} trámite(s) como pagados por fuera? Saldrán de este lote y quedarán en la pestaña "Pagados por fuera".`)) return;
    setBusy(true);
    try {
      const r = await axios.post('/api/pagos-transito/pagados-fuera', {
        tramite_ids: liquidar.sel, motivo: liquidar.fueraMotivo, referencia: liquidar.fueraRef, fecha_pago: liquidar.fueraFecha || undefined
      });
      flash(r.data.message);
      setPfRefresh(n => n + 1);
      setLiquidar(l => ({ ...l, sel: [], fueraMotivo: '', fueraRef: '', fueraFecha: '' }));
      await calcularPreview(liquidar.transito_id, liquidar.fecha);
    } catch (e) {
      const det = e?.response?.data?.errores;
      fail(e, 'Error al marcar los trámites');
      if (Array.isArray(det) && det.length) setError(`${e.response.data.message} ${det.map(x => `#${x.tramite_id}: ${x.motivo}`).join(' · ')}`);
    } finally {
      setBusy(false);
    }
  };

  // Agrega placas (trámites recibidos por correo) a un lote que todavía no se ha pagado
  const validarPlacasLote = async () => {
    if (!String(addPl.texto || '').trim()) return;
    setAddPl(a => ({ ...a, validando: true }));
    try {
      const r = await axios.post(`/api/pagos-transito/lotes/${detalle.id}/placas`, { placas: addPl.texto, validar_solo: true });
      setAddPl(a => ({ ...a, manuales: r.data.manuales, validando: false }));
    } catch (e) {
      fail(e, 'Error al validar las placas');
      setAddPl(a => ({ ...a, validando: false }));
    }
  };
  const agregarPlacasLote = async () => {
    const validas = addPl.manuales.filter(m => m.valido);
    if (!validas.length || !String(addPl.motivo || '').trim()) return;
    setBusy(true);
    try {
      const r = await axios.post(`/api/pagos-transito/lotes/${detalle.id}/placas`, { placas: validas.map(m => m.placa), motivo: addPl.motivo });
      flash(r.data.message);
      fetchLotes();
      await abrirDetalle({ id: detalle.id });
    } catch (e) {
      fail(e, 'Error al agregar las placas');
    } finally {
      setBusy(false);
    }
  };

  // Saca trámites de un lote que todavía no se ha pagado y los deja como "pagados por fuera"
  const sacarDelLote = async () => {
    if (!detFuera.sel.length || !String(detFuera.motivo || '').trim()) return;
    if (!window.confirm(`¿Sacar ${detFuera.sel.length} trámite(s) del lote ${detalle.referencia} y marcarlos como pagados por fuera? El total del lote se recalcula.`)) return;
    setBusy(true);
    try {
      const r = await axios.post('/api/pagos-transito/pagados-fuera', {
        tramite_ids: detFuera.sel, motivo: detFuera.motivo, referencia: detFuera.ref, fecha_pago: detFuera.fecha || undefined
      });
      flash(r.data.message);
      setPfRefresh(n => n + 1);
      fetchLotes();
      await abrirDetalle({ id: detalle.id });
    } catch (e) {
      const det = e?.response?.data?.errores;
      fail(e, 'Error al sacar los trámites del lote');
      if (Array.isArray(det) && det.length) setError(`${e.response.data.message} ${det.map(x => `#${x.tramite_id}: ${x.motivo}`).join(' · ')}`);
    } finally {
      setBusy(false);
    }
  };

  // ---------- Placas manuales (trámites por correo) ----------
  const validarManuales = async () => {
    const placas = String(liquidar.manualTexto || '').split(/[\s,;]+/).map(p => p.trim()).filter(Boolean);
    if (!liquidar.transito_id) { setError('Seleccione primero el tránsito'); return; }
    if (placas.length === 0) { setLiquidar(l => ({ ...l, manuales: [] })); return; }
    setLiquidar(l => ({ ...l, validandoManuales: true }));
    try {
      const r = await axios.post('/api/pagos-transito/lotes/validar-manuales', { transito_id: liquidar.transito_id, fecha: liquidar.fecha, placas });
      setLiquidar(l => ({ ...l, manuales: r.data, validandoManuales: false }));
    } catch (e) {
      fail(e, 'Error al validar las placas');
      setLiquidar(l => ({ ...l, validandoManuales: false }));
    }
  };

  // ---------- Detalle ----------
  const abrirDetalle = async (lote) => {
    try { setDetalle((await axios.get(`/api/pagos-transito/lotes/${lote.id}`)).data); setDetFuera({ sel: [], motivo: '', ref: '', fecha: '' }); setAddPl({ texto: '', motivo: '', manuales: [], validando: false }); } catch (e) { fail(e, 'Error al abrir el lote'); }
  };

  const exportarLote = (d) => {
    const itemsVigentes = d.estado === 'anulado' ? d.items : d.items.filter(i => !i.anulado);
    const rows = itemsVigentes.map((i, idx) => ({
      '#': idx + 1,
      'Referencia de Pago': d.referencia,
      'Trámite': i.origen === 'manual' ? 'MANUAL (correo)' : i.tramite_id,
      'CUPL': i.cupl_number || '',
      'Motivo manual': i.motivo_manual || '',
      'Agregada por': i.agregado_por || '',
      'Placa': i.placa || '—',
      'Tipo de Vehículo': TIPOS[i.vehicle_type] || i.vehicle_type || '—',
      'Tipo de Trámite': i.tipo_tramite || '—',
      'Concesionario': i.concesionario || '—',
      'Propietario': i.propietario || '—',
      'Fecha Ejecución': fFechaHora(i.fecha_ejecucion),
      'Valor': Number(i.valor),
      'Estado del Lote': ESTADOS[d.estado]?.label || d.estado,
      'Cuenta Origen': d.cuenta_origen_texto || '—',
      'Cuenta Destino': d.cuenta_destino_texto || '—',
      'Fecha de Pago': d.fecha_pago ? fFecha(d.fecha_pago) : '—'
    }));
    rows.push({ '#': '', 'Referencia de Pago': 'TOTAL', 'Valor': Number(d.valor_total) });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Lote');
    XLSX.writeFile(wb, `Lote_${d.referencia}_${(d.transito_name || '').replace(/\s+/g, '_')}.xlsx`);
  };

  const exportarListado = () => {
    if (lotes.length === 0) { alert('No hay lotes para exportar.'); return; }
    const rows = lotes.map(l => ({
      'Referencia': l.referencia, 'Tránsito': l.transito_name, 'Fecha Ejecución': fFecha(l.fecha_ejecucion),
      'Trámites': l.cantidad, 'Valor Total': Number(l.valor_total), 'Estado': ESTADOS[l.estado]?.label || l.estado,
      'Cuenta Origen': l.cuenta_origen_texto || '—', 'Cuenta Destino': l.cuenta_destino_texto || '—',
      'Fecha Pago': l.fecha_pago ? fFecha(l.fecha_pago) : '—', 'Generado por': l.created_by_username || '—'
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Lotes');
    XLSX.writeFile(wb, `Pagos_Transitos_${hoy()}.xlsx`);
  };

  const descargarSoporte = async (lote) => {
    try {
      const r = await axios.get(`/api/pagos-transito/lotes/${lote.id}/soporte`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = lote.soporte_nombre_original || `soporte_${lote.referencia}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      fail(e, 'Error al descargar el soporte');
    }
  };

  // ---------- Pago ----------
  const abrirPago = (lote) => {
    const destinos = cuentas.transito.filter(c => c.activa && c.transito_id === lote.transito_id);
    const origenes = cuentas.empresa.filter(c => c.activa);
    setPago({
      lote, file: null, fecha_pago: hoy(), observaciones: '',
      cuenta_origen_id: origenes.length === 1 ? String(origenes[0].id) : '',
      cuenta_destino_id: destinos.length === 1 ? String(destinos[0].id) : ''
    });
  };
  const confirmarPago = async (e) => {
    e.preventDefault();
    if (!pago.file) { setError('Debe adjuntar el soporte de la transacción bancaria'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('soporte', pago.file);
      fd.append('cuenta_origen_id', pago.cuenta_origen_id);
      fd.append('cuenta_destino_id', pago.cuenta_destino_id);
      fd.append('fecha_pago', pago.fecha_pago);
      fd.append('observaciones', pago.observaciones);
      const r = await axios.post(`/api/pagos-transito/lotes/${pago.lote.id}/pago`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      flash(r.data.message);
      setPago(null);
      setDetalle(null);
      fetchLotes();
    } catch (err) {
      fail(err, 'Error al registrar el pago');
    } finally {
      setBusy(false);
    }
  };

  // ---------- Anular ----------
  const confirmarAnular = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`/api/pagos-transito/lotes/${anular.lote.id}/anular`, { motivo: anular.motivo });
      flash(r.data.message);
      setAnular(null);
      setDetalle(null);
      fetchLotes();
    } catch (err) {
      fail(err, 'Error al anular el lote');
    } finally {
      setBusy(false);
    }
  };

  const currentLotes = lotes.slice((page - 1) * perPage, page * perPage);

  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content">
        <Navbar title="🏦 Pagos a Tránsitos" />
        <div className="page-content">
          {error && <div className="error-banner" role="alert">{error}</div>}
          {success && <div className="success-banner" role="status" style={{ background: '#dcfce7', color: '#166534', padding: '12px 16px', borderRadius: '10px', marginBottom: '14px' }}>{success}</div>}

          <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <div className="header-title">
              <h2>Pagos a Tránsitos</h2>
              <p className="subtitle">{isGestor ? 'Liquidación diaria de trámites ejecutados y registro de pagos' : 'Lotes y pagos de su organismo de tránsito'}</p>
            </div>
            {isGestor && tab === 'lotes' && (
              <button type="button" onClick={abrirLiquidar} style={btn('#2563eb')}>💸 Liquidar pago a tránsito</button>
            )}
          </div>

          {isGestor && (
            <div role="tablist" style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
              {[['lotes', '📦 Lotes'], ['pagados', '✅ Pagados por fuera'], ['cuentas', '🏧 Cuentas'], ['tarifas', '💲 Tarifas']].map(([k, l]) => (
                <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
                  style={{ ...btn(tab === k ? '#1e293b' : '#e2e8f0'), color: tab === k ? '#fff' : '#334155' }}>{l}</button>
              ))}
            </div>
          )}

          {tab === 'lotes' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                {['generado', 'pagado'].map(k => (
                  <div key={k} style={{ padding: '14px 18px', borderRadius: '12px', background: ESTADOS[k].bg, color: ESTADOS[k].color }}>
                    <div style={{ fontSize: '12px', fontWeight: 700 }}>{ESTADOS[k].label}</div>
                    <div style={{ fontSize: '22px', fontWeight: 800 }}>{resumen[k].n} lote(s)</div>
                    <div style={{ fontSize: '13px', fontWeight: 600 }}>{money(resumen[k].v)}</div>
                  </div>
                ))}
              </div>

              <div className="dashboard-filters" style={{
                background: 'var(--card-bg)', padding: '16px 24px', borderRadius: '14px', marginBottom: '20px',
                boxShadow: 'var(--card-shadow)', border: '1px solid var(--card-border)',
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '14px', alignItems: 'end'
              }}>
                {transitos.length > 1 && (
                  <Field id="pt-f-tr" label="Tránsito">
                    <select id="pt-f-tr" value={fTransito} onChange={e => { setFTransito(e.target.value); setPage(1); }} style={inputStyle}>
                      <option value="todos">Todos</option>
                      {transitos.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </Field>
                )}
                <Field id="pt-f-es" label="Estado">
                  <select id="pt-f-es" value={fEstado} onChange={e => { setFEstado(e.target.value); setPage(1); }} style={inputStyle}>
                    <option value="todos">Todos</option>
                    {Object.entries(ESTADOS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </Field>
                <Field id="pt-f-de" label="Ejecución desde">
                  <input id="pt-f-de" type="date" value={fDesde} onChange={e => { setFDesde(e.target.value); setPage(1); }} style={inputStyle} />
                </Field>
                <Field id="pt-f-ha" label="Ejecución hasta">
                  <input id="pt-f-ha" type="date" value={fHasta} onChange={e => { setFHasta(e.target.value); setPage(1); }} style={inputStyle} />
                </Field>
                <Field id="pt-f-bu" label="Buscar">
                  <input id="pt-f-bu" type="text" placeholder="Referencia, placa, trámite..." value={fBuscar} onChange={e => { setFBuscar(e.target.value); setPage(1); }} style={inputStyle} />
                </Field>
                <button type="button" onClick={exportarListado} style={btn('#10b981')}>📥 Exportar</button>
              </div>

              {loading ? <p className="loading">Cargando lotes...</p> : (
                <div className="table-wrapper">
                  <div className="table-responsive">
                    <table className="users-table">
                      <thead>
                        <tr>
                          <th>Referencia</th><th>Tránsito</th><th>Fecha Ejecución</th><th>Trámites</th>
                          <th>Valor Total</th><th>Estado</th><th>Pago</th><th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentLotes.map(l => {
                          const es = ESTADOS[l.estado] || {};
                          return (
                            <tr key={l.id}>
                              <td data-label="Referencia"><strong style={{ fontFamily: 'monospace' }}>{l.referencia}</strong></td>
                              <td data-label="Tránsito">{l.transito_name}</td>
                              <td data-label="Fecha Ejecución">{fFecha(l.fecha_ejecucion)}</td>
                              <td data-label="Trámites">{l.cantidad}</td>
                              <td data-label="Valor Total"><strong>{money(l.valor_total)}</strong></td>
                              <td data-label="Estado"><span style={{ padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 700, color: es.color, background: es.bg }}>{es.label}</span></td>
                              <td data-label="Pago" style={{ fontSize: '12px' }}>
                                {l.estado === 'pagado' ? <>{fFecha(l.fecha_pago)}<br /><span style={{ color: '#64748b' }}>{l.pagado_by_username}</span></>
                                  : l.estado === 'anulado' ? <span style={{ color: '#64748b' }} title={l.motivo_anulacion}>Anulado: {l.motivo_anulacion}</span> : '—'}
                              </td>
                              <td data-label="Acciones">
                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                  <button type="button" onClick={() => abrirDetalle(l)} style={smallBtn('#2563eb')}>👁️ Ver</button>
                                  {isGestor && l.estado === 'generado' && <button type="button" onClick={() => abrirPago(l)} style={smallBtn('#16a34a')}>💳 Reportar pago</button>}
                                  {isSuperadmin && l.estado === 'generado' && <button type="button" onClick={() => setAnular({ lote: l, motivo: '' })} style={smallBtn('#dc2626')}>Anular</button>}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {lotes.length === 0 && <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No hay lotes con los filtros seleccionados.</div>}
                  <Pagination currentPage={page} totalItems={lotes.length} itemsPerPage={perPage} onPageChange={setPage}
                    onItemsPerPageChange={(n) => { setPerPage(n); setPage(1); }} />
                </div>
              )}
            </>
          )}

          {tab === 'pagados' && isGestor && (
            <PagadosFueraTab transitos={transitos} isSuperadmin={isSuperadmin} refreshKey={pfRefresh} flash={flash} fail={fail} />
          )}

          {tab === 'cuentas' && isGestor && (
            <CuentasTab cuentas={cuentas} transitos={transitos} onChange={fetchCuentas} flash={flash} fail={fail} />
          )}

          {tab === 'tarifas' && isGestor && (
            <TarifasTab tarifas={tarifas} transitos={transitos} onChange={fetchTarifas} flash={flash} fail={fail} />
          )}
        </div>
      </div>

      {/* MODAL LIQUIDAR */}
      {liquidar && (
        <Modal title="💸 Liquidar pago a tránsito" onClose={() => setLiquidar(null)} width={860}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
            <Field id="lq-tr" label="Tránsito">
              <select id="lq-tr" value={liquidar.transito_id} style={inputStyle}
                onChange={e => { const v = e.target.value; setLiquidar(l => ({ ...l, transito_id: v, manuales: [] })); calcularPreview(v, liquidar.fecha); }}>
                <option value="">Seleccione...</option>
                {transitos.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </Field>
            <Field id="lq-fe" label="Trámites ejecutados el día">
              <input id="lq-fe" type="date" value={liquidar.fecha} max={hoy()} style={inputStyle}
                onChange={e => { const v = e.target.value; setLiquidar(l => ({ ...l, fecha: v, manuales: [] })); calcularPreview(liquidar.transito_id, v); }} />
            </Field>
          </div>
          <p style={{ fontSize: '12px', color: '#64748b', marginTop: 0 }}>
            Entran los trámites ejecutados ese día que no estén en otro lote. El aplicativo genera la referencia que debe usarse en la transferencia.
          </p>
          {liquidar.loading && <p className="loading">Calculando...</p>}
          {liquidar.preview && !liquidar.loading && (
            <>
              <div style={{ display: 'flex', gap: '20px', padding: '12px 16px', background: '#eff6ff', borderRadius: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
                <span><strong>{liquidar.preview.cantidad}</strong> trámite(s)</span>
                <span>Total: <strong>{money(liquidar.preview.valor_total)}</strong></span>
                {liquidar.preview.sin_tarifa > 0 && <span style={{ color: '#b91c1c', fontWeight: 700 }}>⚠️ {liquidar.preview.sin_tarifa} sin tarifa configurada</span>}
              </div>
              {liquidar.preview.cantidad > 0 && (
                <div className="table-responsive" style={{ maxHeight: '320px', overflowY: 'auto' }}>
                  <table className="users-table">
                    <thead><tr>
                      <th style={{ width: '32px' }}><input type="checkbox" aria-label="Seleccionar todos" checked={liquidar.sel.length > 0 && liquidar.sel.length === liquidar.preview.items.length} onChange={toggleSelTodos} /></th>
                      <th>Trámite</th><th>Placa</th><th>Tipo</th><th>Trámite</th><th>Concesionario</th><th>Valor</th></tr></thead>
                    <tbody>
                      {liquidar.preview.items.map(i => (
                        <tr key={i.tramite_id} style={i.valor === null ? { background: '#fee2e2' } : (liquidar.sel.includes(i.tramite_id) ? { background: '#ecfdf5' } : undefined)}>
                          <td><input type="checkbox" aria-label={`Seleccionar trámite ${i.tramite_id}`} checked={liquidar.sel.includes(i.tramite_id)} onChange={() => toggleSel(i.tramite_id)} /></td>
                          <td>#{i.tramite_id}</td><td>{i.placa || '—'}</td><td>{TIPOS[i.vehicle_type] || i.vehicle_type || '—'}</td>
                          <td>{i.tipo_tramite}</td><td>{i.concesionario || '—'}</td>
                          <td>{i.valor === null ? <span style={{ color: '#b91c1c' }}>Sin tarifa</span> : money(i.valor)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {liquidar.preview.cantidad > 0 && liquidar.sel.length > 0 && (
                <div style={{ marginTop: '12px', padding: '14px', border: '1px solid #10b981', borderRadius: '10px', background: '#f0fdf4' }}>
                  <h4 style={{ margin: '0 0 6px', fontSize: '14px' }}>✅ Marcar {liquidar.sel.length} trámite(s) como pagados por fuera</h4>
                  <p style={{ fontSize: '12px', color: '#166534', margin: '0 0 10px' }}>
                    Para trámites que ya se le pagaron al tránsito antes del módulo. Salen de este lote y quedan en la pestaña "Pagados por fuera" como historial.
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '10px' }}>
                    <Field id="pf-mot" label="Motivo (obligatorio)">
                      <input id="pf-mot" type="text" style={inputStyle} value={liquidar.fueraMotivo} placeholder="Ej. Pagado al tránsito antes del módulo"
                        onChange={e => { const v = e.target.value; setLiquidar(l => ({ ...l, fueraMotivo: v })); }} />
                    </Field>
                    <Field id="pf-ref" label="Referencia del pago (opcional)">
                      <input id="pf-ref" type="text" style={inputStyle} value={liquidar.fueraRef}
                        onChange={e => { const v = e.target.value; setLiquidar(l => ({ ...l, fueraRef: v })); }} />
                    </Field>
                    <Field id="pf-fec" label="Fecha del pago (opcional)">
                      <input id="pf-fec" type="date" max={hoy()} style={inputStyle} value={liquidar.fueraFecha}
                        onChange={e => { const v = e.target.value; setLiquidar(l => ({ ...l, fueraFecha: v })); }} />
                    </Field>
                  </div>
                  <button type="button" style={{ ...btn('#059669'), marginTop: '10px' }} disabled={busy || !String(liquidar.fueraMotivo || '').trim()} onClick={marcarPagadosFuera}>
                    {busy ? 'Guardando...' : 'Marcar como pagados por fuera'}
                  </button>
                </div>
              )}
            </>
          )}
          {/* Placas manuales: trámites que llegaron por correo (sin trámite en el aplicativo) */}
          {liquidar.transito_id && (() => {
            const mans = liquidar.manuales || [];
            const validas = mans.filter(m => m.valido);
            const invalidas = mans.filter(m => !m.valido);
            return (
              <div style={{ marginTop: '18px', padding: '14px', border: '1px dashed #f59e0b', borderRadius: '10px', background: '#fffbeb' }}>
                <h4 style={{ margin: '0 0 6px', fontSize: '14px' }}>📧 Agregar placas manualmente (trámites recibidos por correo)</h4>
                <p style={{ fontSize: '12px', color: '#92400e', margin: '0 0 10px' }}>
                  Solo se aceptan placas con CUPL de matrícula <strong>pagado en el aplicativo</strong>, que no tengan trámite en el aplicativo y que no estén en otro lote. Queda registrado quién las agregó y por qué.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <Field id="lq-man" label="Placas (separadas por coma, espacio o línea)">
                    <textarea id="lq-man" rows={3} value={liquidar.manualTexto} style={inputStyle} placeholder="Ej. ABC12D, XYZ34E"
                      onChange={e => { const v = e.target.value; setLiquidar(l => ({ ...l, manualTexto: v, manuales: [] })); }} />
                  </Field>
                  <Field id="lq-mot" label="Motivo (obligatorio si agrega placas)">
                    <textarea id="lq-mot" rows={3} value={liquidar.motivoManual} style={inputStyle} placeholder="Ej. Trámite particular recibido por correo"
                      onChange={e => { const v = e.target.value; setLiquidar(l => ({ ...l, motivoManual: v })); }} />
                  </Field>
                </div>
                <button type="button" onClick={validarManuales} disabled={liquidar.validandoManuales || !String(liquidar.manualTexto || '').trim()}
                  style={{ ...smallBtn('#d97706'), marginTop: '8px' }}>
                  {liquidar.validandoManuales ? 'Validando...' : '🔍 Validar placas'}
                </button>
                {mans.length > 0 && (
                  <div className="table-responsive" style={{ marginTop: '10px', maxHeight: '220px', overflowY: 'auto' }}>
                    <table className="users-table">
                      <thead><tr><th>Placa</th><th>Resultado</th><th>CUPL pagado</th><th>Trámite</th><th>Valor</th></tr></thead>
                      <tbody>
                        {mans.map((m, i) => (
                          <tr key={`${m.placa}-${i}`} style={{ background: m.valido ? '#f0fdf4' : '#fef2f2' }}>
                            <td><strong>{m.placa}</strong></td>
                            <td style={{ fontSize: '12px' }}>
                              {m.valido ? <span style={{ color: '#15803d', fontWeight: 700 }}>✅ Válida</span> : <span style={{ color: '#b91c1c', fontWeight: 700 }}>❌ {m.motivo_rechazo}</span>}
                              {m.advertencia && <div style={{ color: '#b45309' }}>⚠️ {m.advertencia}</div>}
                            </td>
                            <td style={{ fontSize: '12px', fontFamily: 'monospace' }}>{m.cupl_number || '—'}</td>
                            <td style={{ fontSize: '12px' }}>{m.tipo_tramite || '—'}</td>
                            <td>{m.valor ? money(m.valor) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {validas.length > 0 && (
                  <p style={{ fontSize: '13px', margin: '8px 0 0' }}>
                    Se agregarán <strong>{validas.length}</strong> placa(s) manual(es) por <strong>{money(validas.reduce((s, m) => s + m.valor, 0))}</strong>
                    {invalidas.length > 0 && <span style={{ color: '#b91c1c' }}> · {invalidas.length} no válida(s) se omiten</span>}
                  </p>
                )}
              </div>
            );
          })()}

          {(() => {
            const validas = (liquidar.manuales || []).filter(m => m.valido);
            const cantApp = liquidar.preview?.cantidad || 0;
            const faltaMotivo = validas.length > 0 && !String(liquidar.motivoManual || '').trim();
            const sinValidar = String(liquidar.manualTexto || '').trim() && (liquidar.manuales || []).length === 0;
            return (
              <>
                {(liquidar.preview || validas.length > 0) && (
                  <p style={{ fontSize: '14px', fontWeight: 700, margin: '12px 0 0' }}>
                    Total del lote: {money((liquidar.preview?.valor_total || 0) + validas.reduce((s, m) => s + m.valor, 0))} · {cantApp + validas.length} placa(s)
                  </p>
                )}
                {faltaMotivo && <p style={{ color: '#b91c1c', fontSize: '12px', margin: '4px 0 0' }}>Escriba el motivo de las placas manuales.</p>}
                {sinValidar && <p style={{ color: '#b45309', fontSize: '12px', margin: '4px 0 0' }}>Valide las placas manuales antes de generar el lote.</p>}
                <div className="modal-actions" style={{ marginTop: '16px' }}>
                  <button type="button" className="btn-secondary" onClick={() => setLiquidar(null)}>Cancelar</button>
                  <button type="button" style={btn('#2563eb')} onClick={confirmarLiquidar}
                    disabled={busy || !liquidar.preview || (cantApp === 0 && validas.length === 0) || liquidar.preview.sin_tarifa > 0 || faltaMotivo || sinValidar}>
                    {busy ? 'Generando...' : 'Generar lote'}
                  </button>
                </div>
              </>
            );
          })()}
        </Modal>
      )}

      {/* MODAL DETALLE */}
      {detalle && (
        <Modal title={`Lote ${detalle.referencia}`} onClose={() => setDetalle(null)} width={980}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', padding: '14px', background: '#f8fafc', borderRadius: '10px', fontSize: '13px', marginBottom: '14px' }}>
            <div><strong>Referencia de pago:</strong><br /><span style={{ fontFamily: 'monospace', fontSize: '15px' }}>{detalle.referencia}</span></div>
            <div><strong>Tránsito:</strong><br />{detalle.transito_name}</div>
            <div><strong>Ejecutados el:</strong><br />{fFecha(detalle.fecha_ejecucion)}</div>
            <div><strong>Estado:</strong><br />{ESTADOS[detalle.estado]?.label}</div>
            <div><strong>Trámites:</strong><br />{detalle.cantidad}</div>
            <div><strong>Valor total:</strong><br />{money(detalle.valor_total)}</div>
            {detalle.estado === 'pagado' && <>
              <div><strong>Cuenta origen:</strong><br />{detalle.cuenta_origen_texto}</div>
              <div><strong>Cuenta destino:</strong><br />{detalle.cuenta_destino_texto}</div>
              <div><strong>Valor pagado:</strong><br />{money(detalle.valor_pagado)}</div>
              <div><strong>Fecha de pago:</strong><br />{fFecha(detalle.fecha_pago)}</div>
              {detalle.observaciones_pago && <div><strong>Observaciones:</strong><br />{detalle.observaciones_pago}</div>}
            </>}
            {detalle.estado === 'anulado' && <div><strong>Motivo anulación:</strong><br />{detalle.motivo_anulacion}</div>}
          </div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <button type="button" onClick={() => exportarLote(detalle)} style={smallBtn('#10b981')}>📥 Excel del lote</button>
            {detalle.tiene_soporte && <button type="button" onClick={() => descargarSoporte(detalle)} style={smallBtn('#7c3aed')}>📎 Descargar soporte</button>}
            {isGestor && detalle.estado === 'generado' && <button type="button" onClick={() => abrirPago(detalle)} style={smallBtn('#16a34a')}>💳 Reportar pago</button>}
            {isSuperadmin && detalle.estado === 'generado' && <button type="button" onClick={() => setAnular({ lote: detalle, motivo: '' })} style={smallBtn('#dc2626')}>Anular lote</button>}
          </div>
          <div className="table-responsive" style={{ maxHeight: '380px', overflowY: 'auto' }}>
            <table className="users-table">
              <thead><tr>
                {isGestor && detalle.estado === 'generado' && <th style={{ width: '32px' }} />}
                <th>Trámite</th><th>Placa</th><th>Tipo</th><th>Tipo de Trámite</th><th>Concesionario</th><th>Ejecutado</th><th>Valor</th><th>Referencia</th></tr></thead>
              <tbody>
                {detalle.items.filter(i => detalle.estado === 'generado' ? !i.anulado : true).map(i => (
                  <tr key={i.id} style={i.origen === 'manual' ? { background: '#fffbeb' } : (detFuera.sel.includes(i.tramite_id) ? { background: '#ecfdf5' } : undefined)}>
                    {isGestor && detalle.estado === 'generado' && (
                      <td>{i.origen !== 'manual' && i.tramite_id
                        ? <input type="checkbox" aria-label={`Sacar trámite ${i.tramite_id}`} checked={detFuera.sel.includes(i.tramite_id)}
                            onChange={() => setDetFuera(d => ({ ...d, sel: d.sel.includes(i.tramite_id) ? d.sel.filter(x => x !== i.tramite_id) : [...d.sel, i.tramite_id] }))} />
                        : null}</td>
                    )}
                    <td>
                      {i.origen === 'manual'
                        ? <span title={`Agregada por ${i.agregado_por || '—'} · Motivo: ${i.motivo_manual || '—'} · CUPL ${i.cupl_number || '—'}`}
                            style={{ fontSize: '11px', fontWeight: 700, color: '#b45309' }}>📧 Manual</span>
                        : `#${i.tramite_id}`}
                    </td>
                    <td>{i.placa ? <span className="plate-badge-sm">{i.placa}</span> : '—'}</td>
                    <td>{TIPOS[i.vehicle_type] || '—'}</td><td>{i.tipo_tramite}</td><td>{i.concesionario || '—'}</td>
                    <td>{fFechaHora(i.fecha_ejecucion)}</td><td>{money(i.valor)}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{detalle.referencia}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {isGestor && detalle.estado === 'generado' && (() => {
            const mans = addPl.manuales || [];
            const validas = mans.filter(m => m.valido);
            return (
              <div style={{ marginTop: '12px', padding: '14px', border: '1px dashed #f59e0b', borderRadius: '10px', background: '#fffbeb' }}>
                <h4 style={{ margin: '0 0 6px', fontSize: '14px' }}>➕ Agregar placas a este lote</h4>
                <p style={{ fontSize: '12px', color: '#92400e', margin: '0 0 10px' }}>
                  El lote aún no está pagado. Se aceptan placas con CUPL de matrícula <strong>pagado en el aplicativo</strong> que no tengan trámite en el aplicativo y no estén en otro lote. El total del lote se recalcula.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <Field id="ap-pl" label="Placas (separadas por coma, espacio o línea)">
                    <textarea id="ap-pl" rows={2} value={addPl.texto} style={inputStyle} placeholder="Ej. ABC12D, XYZ34E"
                      onChange={e => { const v = e.target.value; setAddPl(a => ({ ...a, texto: v, manuales: [] })); }} />
                  </Field>
                  <Field id="ap-mo" label="Motivo (obligatorio)">
                    <textarea id="ap-mo" rows={2} value={addPl.motivo} style={inputStyle} placeholder="Ej. Trámite particular recibido por correo"
                      onChange={e => { const v = e.target.value; setAddPl(a => ({ ...a, motivo: v })); }} />
                  </Field>
                </div>
                <button type="button" onClick={validarPlacasLote} disabled={addPl.validando || !String(addPl.texto || '').trim()} style={{ ...smallBtn('#d97706'), marginTop: '8px' }}>
                  {addPl.validando ? 'Validando...' : '🔍 Validar placas'}
                </button>
                {mans.length > 0 && (
                  <div className="table-responsive" style={{ marginTop: '10px', maxHeight: '200px', overflowY: 'auto' }}>
                    <table className="users-table">
                      <thead><tr><th>Placa</th><th>Resultado</th><th>CUPL pagado</th><th>Trámite</th><th>Valor</th></tr></thead>
                      <tbody>
                        {mans.map((m, i) => (
                          <tr key={`${m.placa}-${i}`} style={{ background: m.valido ? '#f0fdf4' : '#fef2f2' }}>
                            <td><strong>{m.placa}</strong></td>
                            <td style={{ fontSize: '12px' }}>
                              {m.valido ? <span style={{ color: '#15803d', fontWeight: 700 }}>✅ Válida</span> : <span style={{ color: '#b91c1c', fontWeight: 700 }}>❌ {m.motivo_rechazo}</span>}
                              {m.advertencia && <div style={{ color: '#b45309' }}>⚠️ {m.advertencia}</div>}
                            </td>
                            <td style={{ fontSize: '12px', fontFamily: 'monospace' }}>{m.cupl_number || '—'}</td>
                            <td style={{ fontSize: '12px' }}>{m.tipo_tramite || '—'}</td>
                            <td>{m.valor ? money(m.valor) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {validas.length > 0 && (
                  <div style={{ marginTop: '10px' }}>
                    <p style={{ fontSize: '13px', margin: '0 0 8px' }}>
                      Se agregarán <strong>{validas.length}</strong> placa(s) por <strong>{money(validas.reduce((s, m) => s + m.valor, 0))}</strong>
                      {mans.length > validas.length && <span style={{ color: '#b91c1c' }}> · {mans.length - validas.length} no válida(s) se omiten</span>}
                    </p>
                    <button type="button" style={btn('#2563eb')} disabled={busy || !String(addPl.motivo || '').trim()} onClick={agregarPlacasLote}>
                      {busy ? 'Agregando...' : 'Agregar al lote'}
                    </button>
                    {!String(addPl.motivo || '').trim() && <span style={{ color: '#b91c1c', fontSize: '12px', marginLeft: '10px' }}>Escriba el motivo.</span>}
                  </div>
                )}
              </div>
            );
          })()}
          {isGestor && detalle.estado === 'generado' && detFuera.sel.length > 0 && (
            <div style={{ marginTop: '12px', padding: '14px', border: '1px solid #10b981', borderRadius: '10px', background: '#f0fdf4' }}>
              <h4 style={{ margin: '0 0 6px', fontSize: '14px' }}>✅ Sacar {detFuera.sel.length} trámite(s) de este lote y marcarlos como pagados por fuera</h4>
              <p style={{ fontSize: '12px', color: '#166534', margin: '0 0 10px' }}>
                El lote aún no está pagado, por eso se pueden sacar. Se recalcula su total y los trámites quedan en la pestaña "Pagados por fuera".
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '10px' }}>
                <Field id="df-mot" label="Motivo (obligatorio)">
                  <input id="df-mot" type="text" style={inputStyle} value={detFuera.motivo} placeholder="Ej. Pagado al tránsito antes del módulo"
                    onChange={e => { const v = e.target.value; setDetFuera(d => ({ ...d, motivo: v })); }} />
                </Field>
                <Field id="df-ref" label="Referencia del pago (opcional)">
                  <input id="df-ref" type="text" style={inputStyle} value={detFuera.ref}
                    onChange={e => { const v = e.target.value; setDetFuera(d => ({ ...d, ref: v })); }} />
                </Field>
                <Field id="df-fec" label="Fecha del pago (opcional)">
                  <input id="df-fec" type="date" max={hoy()} style={inputStyle} value={detFuera.fecha}
                    onChange={e => { const v = e.target.value; setDetFuera(d => ({ ...d, fecha: v })); }} />
                </Field>
              </div>
              <button type="button" style={{ ...btn('#059669'), marginTop: '10px' }} disabled={busy || !String(detFuera.motivo || '').trim()} onClick={sacarDelLote}>
                {busy ? 'Guardando...' : 'Sacar del lote y marcar como pagados por fuera'}
              </button>
            </div>
          )}
        </Modal>
      )}

      {/* MODAL PAGO */}
      {pago && (
        <Modal title={`💳 Reportar pago — ${pago.lote.referencia}`} onClose={() => setPago(null)} width={620}>
          <form onSubmit={confirmarPago}>
            <div style={{ padding: '12px 16px', background: '#eff6ff', borderRadius: '10px', marginBottom: '14px', fontSize: '13px' }}>
              Use como referencia de la transferencia: <strong style={{ fontFamily: 'monospace', fontSize: '15px' }}>{pago.lote.referencia}</strong><br />
              Valor a pagar: <strong>{money(pago.lote.valor_total)}</strong> · {pago.lote.cantidad} trámite(s) · {pago.lote.transito_name}
            </div>
            <div style={{ display: 'grid', gap: '12px' }}>
              <Field id="pg-or" label="Cuenta de origen (empresa)">
                <select id="pg-or" required value={pago.cuenta_origen_id} onChange={e => setPago(p => ({ ...p, cuenta_origen_id: e.target.value }))} style={inputStyle}>
                  <option value="">Seleccione...</option>
                  {cuentas.empresa.filter(c => c.activa).map(c => <option key={c.id} value={c.id}>{c.banco} {c.tipo_cuenta} {c.numero} — {c.titular}</option>)}
                </select>
              </Field>
              <Field id="pg-de" label="Cuenta de destino (tránsito)">
                <select id="pg-de" required value={pago.cuenta_destino_id} onChange={e => setPago(p => ({ ...p, cuenta_destino_id: e.target.value }))} style={inputStyle}>
                  <option value="">Seleccione...</option>
                  {cuentas.transito.filter(c => c.activa && c.transito_id === pago.lote.transito_id).map(c => <option key={c.id} value={c.id}>{c.banco} {c.tipo_cuenta} {c.numero} — {c.titular}</option>)}
                </select>
              </Field>
              {cuentas.transito.filter(c => c.activa && c.transito_id === pago.lote.transito_id).length === 0 &&
                <p style={{ color: '#b91c1c', fontSize: '12px', margin: 0 }}>Este tránsito no tiene cuentas de destino registradas. Agréguela en la pestaña Cuentas.</p>}
              <Field id="pg-fe" label="Fecha del pago">
                <input id="pg-fe" type="date" required max={hoy()} value={pago.fecha_pago} onChange={e => setPago(p => ({ ...p, fecha_pago: e.target.value }))} style={inputStyle} />
              </Field>
              <Field id="pg-so" label="Soporte de la transacción (PDF, PNG o JPG, máx. 10 MB)">
                <input id="pg-so" type="file" required accept=".pdf,.png,.jpg,.jpeg" onChange={e => setPago(p => ({ ...p, file: e.target.files[0] || null }))} style={inputStyle} />
              </Field>
              <Field id="pg-ob" label="Observaciones (opcional)">
                <textarea id="pg-ob" rows={2} value={pago.observaciones} onChange={e => setPago(p => ({ ...p, observaciones: e.target.value }))} style={inputStyle} />
              </Field>
            </div>
            <div className="modal-actions" style={{ marginTop: '16px' }}>
              <button type="button" className="btn-secondary" onClick={() => setPago(null)}>Cancelar</button>
              <button type="submit" style={btn('#16a34a')} disabled={busy}>{busy ? 'Guardando...' : 'Registrar pago'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* MODAL ANULAR */}
      {anular && (
        <Modal title={`Anular lote ${anular.lote.referencia}`} onClose={() => setAnular(null)} width={520}>
          <form onSubmit={confirmarAnular}>
            <p style={{ fontSize: '13px' }}>Los {anular.lote.cantidad} trámite(s) del lote quedarán libres para incluirse en un nuevo lote. Esta acción no se puede deshacer.</p>
            <Field id="an-mo" label="Motivo de la anulación">
              <textarea id="an-mo" rows={3} required value={anular.motivo} onChange={e => setAnular(a => ({ ...a, motivo: e.target.value }))} style={inputStyle} />
            </Field>
            <div className="modal-actions" style={{ marginTop: '16px' }}>
              <button type="button" className="btn-secondary" onClick={() => setAnular(null)}>Cancelar</button>
              <button type="submit" style={btn('#dc2626')} disabled={busy || !anular.motivo.trim()}>{busy ? 'Anulando...' : 'Anular lote'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

// ---------------- Pestaña Cuentas ----------------
const CUENTA_VACIA = { banco: '', tipo_cuenta: 'Ahorros', numero: '', titular: '', nit: '', transito_id: '' };

const CuentasTab = ({ cuentas, transitos, onChange, flash, fail }) => {
  const [form, setForm] = useState(null); // { tipo, ...campos }

  const guardar = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`/api/pagos-transito/cuentas/${form.tipo}`, form);
      flash('Cuenta registrada');
      setForm(null);
      onChange();
    } catch (err) { fail(err, 'Error al registrar la cuenta'); }
  };
  const toggle = async (tipo, c) => {
    try { await axios.put(`/api/pagos-transito/cuentas/${tipo}/${c.id}/estado`, { activa: !c.activa }); onChange(); }
    catch (err) { fail(err, 'Error al actualizar la cuenta'); }
  };

  const Tabla = ({ tipo, rows }) => (
    <div className="table-wrapper" style={{ marginBottom: '24px' }}>
      <div className="table-responsive">
        <table className="users-table">
          <thead><tr>{tipo === 'transito' && <th>Tránsito</th>}<th>Banco</th><th>Tipo</th><th>Número</th><th>Titular</th><th>NIT</th><th>Estado</th><th>Acción</th></tr></thead>
          <tbody>
            {rows.map(c => (
              <tr key={c.id} style={!c.activa ? { opacity: 0.55 } : undefined}>
                {tipo === 'transito' && <td>{c.transito_name}</td>}
                <td>{c.banco}</td><td>{c.tipo_cuenta}</td><td style={{ fontFamily: 'monospace' }}>{c.numero}</td><td>{c.titular}</td><td>{c.nit || '—'}</td>
                <td>{c.activa ? 'Activa' : 'Inactiva'}</td>
                <td><button type="button" onClick={() => toggle(tipo, c)} style={smallBtn(c.activa ? '#64748b' : '#16a34a')}>{c.activa ? 'Desactivar' : 'Activar'}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length === 0 && <div style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>No hay cuentas registradas.</div>}
    </div>
  );

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <h3 style={{ margin: 0 }}>Cuentas de la empresa (origen)</h3>
        <button type="button" onClick={() => setForm({ ...CUENTA_VACIA, tipo: 'empresa' })} style={btn('#2563eb')}>+ Agregar cuenta</button>
      </div>
      <Tabla tipo="empresa" rows={cuentas.empresa} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <h3 style={{ margin: 0 }}>Cuentas de los tránsitos (destino)</h3>
        <button type="button" onClick={() => setForm({ ...CUENTA_VACIA, tipo: 'transito' })} style={btn('#2563eb')}>+ Agregar cuenta</button>
      </div>
      <Tabla tipo="transito" rows={cuentas.transito} />

      {form && (
        <Modal title={form.tipo === 'empresa' ? 'Nueva cuenta de la empresa' : 'Nueva cuenta de tránsito'} onClose={() => setForm(null)} width={560}>
          <form onSubmit={guardar} style={{ display: 'grid', gap: '12px' }}>
            {form.tipo === 'transito' && (
              <Field id="cu-tr" label="Tránsito">
                <select id="cu-tr" required value={form.transito_id} onChange={e => setForm(f => ({ ...f, transito_id: e.target.value }))} style={inputStyle}>
                  <option value="">Seleccione...</option>
                  {transitos.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </Field>
            )}
            <Field id="cu-ba" label="Banco"><input id="cu-ba" required value={form.banco} onChange={e => setForm(f => ({ ...f, banco: e.target.value }))} style={inputStyle} /></Field>
            <Field id="cu-ti" label="Tipo de cuenta">
              <select id="cu-ti" value={form.tipo_cuenta} onChange={e => setForm(f => ({ ...f, tipo_cuenta: e.target.value }))} style={inputStyle}>
                <option>Ahorros</option><option>Corriente</option>
              </select>
            </Field>
            <Field id="cu-nu" label="Número de cuenta"><input id="cu-nu" required value={form.numero} onChange={e => setForm(f => ({ ...f, numero: e.target.value }))} style={inputStyle} /></Field>
            <Field id="cu-tt" label="Titular"><input id="cu-tt" required value={form.titular} onChange={e => setForm(f => ({ ...f, titular: e.target.value }))} style={inputStyle} /></Field>
            <Field id="cu-ni" label="NIT (opcional)"><input id="cu-ni" value={form.nit} onChange={e => setForm(f => ({ ...f, nit: e.target.value }))} style={inputStyle} /></Field>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setForm(null)}>Cancelar</button>
              <button type="submit" style={btn('#2563eb')}>Guardar</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
};

// ---------------- Pestaña Tarifas ----------------
const TarifasTab = ({ tarifas, transitos, onChange, flash, fail }) => {
  const [form, setForm] = useState(null);
  const [importErrors, setImportErrors] = useState([]);

  const guardar = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/pagos-transito/tarifas', form);
      flash('Tarifa registrada');
      setForm(null);
      onChange();
    } catch (err) { fail(err, 'Error al registrar la tarifa'); }
  };
  const toggle = async (t) => {
    try { await axios.put(`/api/pagos-transito/tarifas/${t.id}/estado`, { activa: !t.activa }); onChange(); }
    catch (err) { fail(err, 'Error al actualizar la tarifa'); }
  };

  // Columnas esperadas: Tránsito | Tipo de Vehículo | Tipo de Trámite | Valor | Vigente Desde
  const norm = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z]/g, '');
  const importar = async (file) => {
    setImportErrors([]);
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { cellDates: true });
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' });
      const pick = (r, ...keys) => { const k = Object.keys(r).find(k2 => keys.some(x => norm(k2).includes(x))); return k ? r[k] : ''; };
      const tarifasXls = rows.map(r => {
        const fecha = pick(r, 'vigente', 'desde', 'fecha');
        return {
          transito: pick(r, 'transito', 'organismo'),
          vehicle_type: pick(r, 'vehiculo', 'clase') || null,
          tipo_tramite_patron: pick(r, 'tramite') || null,
          valor: pick(r, 'valor', 'tarifa', 'precio'),
          vigente_desde: fecha instanceof Date ? fecha.toISOString().slice(0, 10) : (fecha || null)
        };
      }).filter(t => t.transito || t.valor);
      const r = await axios.post('/api/pagos-transito/tarifas/importar', { tarifas: tarifasXls });
      flash(r.data.message);
      onChange();
    } catch (err) {
      setImportErrors(err.response?.data?.errores || []);
      fail(err, 'Error al importar el archivo');
    }
  };

  const plantilla = () => {
    const ws = XLSX.utils.json_to_sheet([
      { 'Tránsito': 'ARACATACA', 'Tipo de Vehículo': 'Moto', 'Tipo de Trámite': '', 'Valor': 0, 'Vigente Desde': '' },
      { 'Tránsito': 'ARACATACA', 'Tipo de Vehículo': 'Moto', 'Tipo de Trámite': 'PRENDA', 'Valor': 0, 'Vigente Desde': '' }
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Tarifas');
    XLSX.writeFile(wb, 'Plantilla_Tarifas_Transitos.xlsx');
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
        <div>
          <h3 style={{ margin: 0 }}>Valores a pagar por trámite</h3>
          <p style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 0' }}>
            Se aplica la tarifa más específica (tránsito + tipo de vehículo + tipo de trámite). "Tipo de trámite" es un texto que debe contener el nombre del trámite (ej. PRENDA); vacío = cualquier trámite.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button type="button" onClick={plantilla} style={btn('#64748b')}>📄 Plantilla Excel</button>
          <label style={{ ...btn('#10b981'), display: 'inline-block' }}>
            📤 Importar Excel
            <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) importar(e.target.files[0]); e.target.value = ''; }} />
          </label>
          <button type="button" onClick={() => setForm({ transito_id: '', vehicle_type: '', tipo_tramite_patron: '', valor: '', vigente_desde: '' })} style={btn('#2563eb')}>+ Agregar tarifa</button>
        </div>
      </div>
      {importErrors.length > 0 && (
        <div className="error-banner" role="alert" style={{ marginBottom: '12px' }}>
          <strong>Errores en el archivo:</strong>
          <ul style={{ margin: '6px 0 0 18px' }}>{importErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>
        </div>
      )}
      <div className="table-wrapper">
        <div className="table-responsive">
          <table className="users-table">
            <thead><tr><th>Tránsito</th><th>Tipo de Vehículo</th><th>Tipo de Trámite</th><th>Valor</th><th>Vigente Desde</th><th>Estado</th><th>Acción</th></tr></thead>
            <tbody>
              {tarifas.map(t => (
                <tr key={t.id} style={!t.activa ? { opacity: 0.55 } : undefined}>
                  <td>{t.transito_name}</td><td>{TIPOS[t.vehicle_type] || 'Cualquiera'}</td><td>{t.tipo_tramite_patron || 'Cualquiera'}</td>
                  <td><strong>{money(t.valor)}</strong></td><td>{t.vigente_desde ? fFecha(t.vigente_desde) : 'Siempre'}</td>
                  <td>{t.activa ? 'Activa' : 'Inactiva'}</td>
                  <td><button type="button" onClick={() => toggle(t)} style={smallBtn(t.activa ? '#64748b' : '#16a34a')}>{t.activa ? 'Desactivar' : 'Activar'}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {tarifas.length === 0 && <div style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>No hay tarifas registradas. Puede importarlas desde Excel.</div>}
      </div>

      {form && (
        <Modal title="Nueva tarifa" onClose={() => setForm(null)} width={520}>
          <form onSubmit={guardar} style={{ display: 'grid', gap: '12px' }}>
            <Field id="ta-tr" label="Tránsito">
              <select id="ta-tr" required value={form.transito_id} onChange={e => setForm(f => ({ ...f, transito_id: e.target.value }))} style={inputStyle}>
                <option value="">Seleccione...</option>
                {transitos.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </Field>
            <Field id="ta-ve" label="Tipo de vehículo">
              <select id="ta-ve" value={form.vehicle_type} onChange={e => setForm(f => ({ ...f, vehicle_type: e.target.value }))} style={inputStyle}>
                <option value="">Cualquiera</option>
                {Object.entries(TIPOS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </Field>
            <Field id="ta-tt" label="Tipo de trámite contiene (opcional)">
              <input id="ta-tt" placeholder="Ej. PRENDA" value={form.tipo_tramite_patron} onChange={e => setForm(f => ({ ...f, tipo_tramite_patron: e.target.value }))} style={inputStyle} />
            </Field>
            <Field id="ta-va" label="Valor a pagar por trámite">
              <input id="ta-va" type="number" min="1" step="1" required value={form.valor} onChange={e => setForm(f => ({ ...f, valor: e.target.value }))} style={inputStyle} />
            </Field>
            <Field id="ta-vd" label="Vigente desde (vacío = siempre)">
              <input id="ta-vd" type="date" value={form.vigente_desde} onChange={e => setForm(f => ({ ...f, vigente_desde: e.target.value }))} style={inputStyle} />
            </Field>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setForm(null)}>Cancelar</button>
              <button type="submit" style={btn('#2563eb')}>Guardar</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
};

export default PagosTransito;
